import type { Param } from "@/models/channel.model"
import { create } from "zustand"

type ParamHint = {
  type: "date" | "participant" | "events"
  label: string
  color: string
}

type ParamHintsState = {
  isShowingHints: boolean
  activeParams: ParamHint[]
  showHints: (params: Param[]) => void
  hideHints: () => void
  triggerFlash: (type: "date" | "participant" | "events") => void
  flashingElements: Set<string>
  flashTimeouts: Map<string, NodeJS.Timeout>
  hideTimeout: NodeJS.Timeout | null
  cleanup: () => void
}

export const useParamHintsStore = create<ParamHintsState>((set, get) => ({
  isShowingHints: false,
  activeParams: [],
  flashingElements: new Set(),
  flashTimeouts: new Map(),
  hideTimeout: null,

  showHints: (params: Param[]) => {
    // Clean up any existing timeouts first
    get().cleanup()

    const hints: ParamHint[] = params.map(param => ({
      type: param.key,
      label: param.label,
      color: param.color,
    }))

    set({
      isShowingHints: true,
      activeParams: hints,
      flashingElements: new Set(),
    })

    // Trigger flash for each param type
    hints.forEach((hint) => {
      get().triggerFlash(hint.type)
    })

    // Auto hide after 5 seconds (increased for better UX)
    const hideTimeout = setTimeout(() => {
      get().hideHints()
    }, 5000)
    set({ hideTimeout })
  },

  hideHints: () => {
    // Clean up timeouts before hiding
    get().cleanup()

    set({
      isShowingHints: false,
      activeParams: [],
      flashingElements: new Set(),
    })
  },

  triggerFlash: (type: "date" | "participant" | "events") => {
    const { flashingElements, flashTimeouts } = get()

    // Clear existing timeout for this type if it exists
    const existingTimeout = flashTimeouts.get(type)
    if (existingTimeout) {
      clearTimeout(existingTimeout)
    }

    // Add to flashing elements
    const newFlashing = new Set(flashingElements)
    newFlashing.add(type)
    set({ flashingElements: newFlashing })

    // Remove flash after 4 seconds
    const flashTimeout = setTimeout(() => {
      const currentFlashing = get().flashingElements
      const currentTimeouts = get().flashTimeouts
      const updatedFlashing = new Set(currentFlashing)
      const updatedTimeouts = new Map(currentTimeouts)

      updatedFlashing.delete(type)
      updatedTimeouts.delete(type)

      set({
        flashingElements: updatedFlashing,
        flashTimeouts: updatedTimeouts,
      })
    }, 4000)

    // Store the timeout
    const newTimeouts = new Map(flashTimeouts)
    newTimeouts.set(type, flashTimeout)
    set({ flashTimeouts: newTimeouts })
  },

  cleanup: () => {
    const { flashTimeouts, hideTimeout } = get()

    // Clear all flash timeouts
    flashTimeouts.forEach((timeout) => {
      clearTimeout(timeout)
    })

    // Clear hide timeout
    if (hideTimeout) {
      clearTimeout(hideTimeout)
    }

    // Reset state
    set({
      flashTimeouts: new Map(),
      hideTimeout: null,
      flashingElements: new Set(),
    })
  },
}))
